import {BASE_ENDPOINT} from "../../api/endpoints";
import Cookies from "js-cookie";
import {AgencyProps} from "../../api/model/agency";
import {GENERAL_BACKEND_RESPONSE} from "../../api/model/common";
import {COOKIE_NAME} from "../../constants/default-values";

/**
 * Fetches the agency logo from the server based on the provided agency ID.
 *
 * This asynchronous function sends a GET request to retrieve the agency's logo.
 * It uses an authorization token stored in a cookie to access the resource.
 * If the request is successful, the logo data is returned in a structured object,
 * including its blob URL, array buffer, and file type.
 * If the request fails, a failure status along with null values for the data is returned.
 *
 * @param {AgencyProps["id"]} agencyId - The unique identifier of the agency.
 * @returns {Promise<Object>} A promise that resolves to an object containing:
 *  - `status` (String): Indicates whether the operation succeeded or failed,
 *  - `imageBlob` (String|null): A Blob URL representing the agency logo, or null if fetching failed,
 *  - `arrayBuffer` (ArrayBuffer|null): An ArrayBuffer of the image, or null if fetching failed,
 *  - `fileType` (string|null): The file type extracted from the Content-Type header, or null if unavailable.
 */
export const getAgencyLogo = async (agencyId: AgencyProps["id"]) => {
    try {
        let headers = new Headers();
        const token = Cookies.get(COOKIE_NAME)
        headers.set('Authorization', `Bearer ${token}`);
        const response = await fetch(`${BASE_ENDPOINT}/api/v1/agency/${agencyId}/logo`, {
            method: 'GET',
            headers
        })
        if (response.ok) {
            const contentType = response.headers.get('Content-Type');
            const clonedResponse = response.clone()
            const imageBlob = await response.blob();
            const imageBuffer = await clonedResponse.arrayBuffer()
            const fileType = contentType?.split('/')[1];
            return {
                status: GENERAL_BACKEND_RESPONSE.success,
                imageBlob: URL.createObjectURL(imageBlob),
                arrayBuffer: imageBuffer,
                fileType
            }
        } else {
            return {
                status: GENERAL_BACKEND_RESPONSE.failure,
                imageBlob: null,
                arrayBuffer: null
            }
        }
    } catch (error) {
        console.error(`Failed to get agency logo with error: ${error}`)
    }
}